// apps/web/src/composables/useNotifications.ts
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import { api } from '@/api/client';
import { onNotification } from '@/composables/useSignalRDashboard';
const notifications = ref([]);
const isLoading = ref(false);
let refCount = 0;
let unsubscribe = null;
/** Test-only helper to reset the module-level singleton between test cases. */
export function resetNotifications() {
    notifications.value = [];
    isLoading.value = false;
    refCount = 0;
    unsubscribe = null;
}
const refresh = useDebounceFn(async () => {
    isLoading.value = true;
    try {
        const { data, error } = await api.GET('/v1/notifications', {
            params: { query: { unread: true, limit: 20 } },
        });
        if (!error && data)
            notifications.value = data;
    }
    finally {
        isLoading.value = false;
    }
}, 250);
async function markRead(id) {
    await api.PATCH('/v1/notifications/{id}/read', { params: { path: { id } } });
    const n = notifications.value.find((x) => x.id === id);
    if (n) {
        Object.assign(n, { isRead: true, readAt: new Date().toISOString() });
    }
}
async function markAllRead() {
    await api.POST('/v1/notifications/mark-all-read', {});
    notifications.value.forEach((n) => Object.assign(n, { isRead: true }));
}
/**
 * Per-user notification inbox. Singleton — multiple consumers share one fetch + one subscription.
 * Matches the useNavCounts pattern.
 */
export function useNotifications() {
    onMounted(() => {
        refCount++;
        if (refCount === 1) {
            void refresh();
            unsubscribe = onNotification(() => void refresh());
        }
    });
    onBeforeUnmount(() => {
        refCount--;
        if (refCount === 0 && unsubscribe) {
            unsubscribe();
            unsubscribe = null;
        }
    });
    const unreadCount = computed(() => notifications.value.filter((n) => !n.isRead).length);
    return { notifications, unreadCount, isLoading, refresh, markRead, markAllRead };
}
